// Flytta (spec §4.3.1, WP6; mockup receiving-v1/move.html): an ad-hoc move of a pallet or a
// kolli to another place. Same shape as put-away, two scans, no proposal. A kolli that sits
// on a pallet is taken off it (Disaggregated) before it moves; the screen warns first and
// offers to move the whole pallet instead.
//
// Onto a pallet (owner 2026-11-02): racking has beams, not shelves, so a kolli there must sit on a
// pallet. When the scanned place takes only pallets, or pallets already stand there, the screen
// asks: onto one of those pallets, onto a new pallet issued here, or loose on the place (where
// allowed). "Ny pall här" at the destination step builds a pallet where the kolli stands.
//
// Loose stock (mockup admin-v1/stock-mirror, owner 2026-10-30): the first scan may be a
// location. The screen then lists what stands there — cartons, which move whole by their own
// label, and loose stock, which has no label and is picked from the list with a quantity
// (everything by default). Part of a loose quantity is cut off (split) at its place and the
// piece moves; the rest stays.

import { MOVE, applyInput, classOf, identityKey, parseScan, startRun, uuidv7, type EventEnvelope, type EventType, type Effect, type GS1Elements, type RunState, type ScanInput } from "@trail/core";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Api, effectiveSettings as getSettings, type AutomationEventIn, type BundleLocation, type ContainerSummary, type StepBatch } from "../api/client.js";
import { CameraView } from "../CameraView.js";
import { fmtQty, t } from "../i18n.js";
import { buildEmittedBatch } from "../receive/events.js";
import { pending, startQueueWorker, submitStep, subscribe, type QueuedStep } from "../receive/queue.js";
import { missingLocationMessage } from "../receive/location-message.js";
import { storageMismatchAny, buildRegistry, cacheBundle, loadCachedBundle, plainElements, holdsOf, palletCapacityOf, type Holds, type Registry, findLocation } from "../receive/registry.js";
import { isGs1Symbology, type CodeState } from "../scan/camera.js";
import { feedback, unlockAudio } from "../scan/feedback.js";
import { attachWedge } from "../scan/wedge.js";
import { Actions, Banner, Btn, LabelStrip, Prompt, Rows, ScanBar, Sheet, Strip, focusScan, type BannerData, type LabelCell, type RowData } from "../ui/components.jsx";

const FLOW = MOVE;
const SCHEME = "gtin+lot" as const;
const shortSscc = (sscc: string) => `${sscc.slice(0, 4)}…${sscc.slice(-4)}`;
/** The container endpoint lists children; the by-identity one counts them. */
const kidsOf = (c: { children: number | unknown[] }) => (Array.isArray(c.children) ? c.children.length : c.children);

interface Unit {
  container: ContainerSummary;
  elements: GS1Elements;
  /** The pallet the kolli sits on, when any. */
  parent: ContainerSummary | null;
}

export function Move({ onClose }: { onClose: () => void }) {
  const settings = useMemo(getSettings, []);
  const [registry, setRegistry] = useState<Registry | null>(() => {
    const cached = loadCachedBundle();
    return cached ? buildRegistry(cached) : null;
  });
  const [queue, setQueue] = useState<QueuedStep[]>(pending);
  const [online, setOnline] = useState(navigator.onLine);
  const [seq, setSeq] = useState(0);
  const [carry, setCarry] = useState<BannerData | null>(null);

  useEffect(() => {
    startQueueWorker();
    Api.bundle()
      .then((b) => {
        cacheBundle(b);
        setRegistry(buildRegistry(b));
      })
      .catch(() => undefined);
    const on = () => setOnline(true);
    const off = () => setOnline(false);
    window.addEventListener("online", on);
    window.addEventListener("offline", off);
    const unsub = subscribe(setQueue);
    return () => {
      window.removeEventListener("online", on);
      window.removeEventListener("offline", off);
      unsub();
    };
  }, []);

  const net = { online, queued: queue.length, stuck: queue.filter((q) => q.lastError && !/fetch|network|abort|5\d\d|401|429/i.test(q.lastError)).length };
  if (!registry) {
    return (
      <div className="screen">
        <Strip left={<b>{t("menu.move")}</b>} {...net} />
        <div className="body"><p className="hint">{t("common.loading")}</p></div>
        <Actions><Btn kind="quiet" icon="back" onClick={onClose}>{t("common.menu")}</Btn></Actions>
      </div>
    );
  }
  return <Run key={seq} registry={registry} settings={settings} net={net} carry={carry} onDone={(banner) => { setCarry(banner); setSeq((s) => s + 1); }} onClose={onClose} />;
}

interface RunProps {
  registry: Registry;
  settings: ReturnType<typeof getSettings>;
  net: { online: boolean; queued: number; stuck: number };
  carry: BannerData | null;
  onDone: (banner: BannerData) => void;
  onClose: () => void;
}

function Run({ registry, settings, net, carry, onDone, onClose }: RunProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [run, setRun] = useState<RunState>(() => startRun(FLOW).state);
  const runRef = useRef(run);
  runRef.current = run;
  const [unit, setUnit] = useState<Unit | null>(null);
  // Location-first path: what stands on the scanned location, and the loose row being chosen.
  const [here, setHere] = useState<{ location: BundleLocation; contents: ContainerSummary[] } | null>(null);
  /** The scanned destination takes pallets, or pallets stand there: the choice sheet (onto which, a new one, or loose). */
  const [onto, setOnto] = useState<{ location: BundleLocation; pallets: ContainerSummary[]; holds: Holds; capacity: number } | null>(null);
  const [issuing, setIssuing] = useState(false);
  const printLabel = async (req: { kind: "pallet-label" | "location-label" | "customer-label"; containerId?: string; locationId?: string }) => {
    try {
      const job = await Api.printJob(req);
      feedback("accepted");
      setBanner({ kind: "ok", label: t("move.printed"), message: t("move.printedMsg", { printer: job.printerName }) });
    } catch (e) {
      feedback("rejected");
      const msg = e instanceof Error ? e.message : String(e);
      setBanner({ kind: "bad", label: t("banner.rejected"), message: /no_printer|no active/i.test(msg) ? t("move.noPrinter") : msg });
    }
  };
  const [loose, setLoose] = useState<{ c: ContainerSummary; qty: string } | null>(null);
  /** When part of a loose quantity moves: the piece to cut off at its place before the move. */
  const partialRef = useRef<{ parent: ContainerSummary; childId: string; qty: string; remaining: string } | null>(null);
  const [banner, setBanner] = useState<BannerData | null>(carry);
  const [busy, setBusy] = useState(false);
  const [camera, setCamera] = useState(false);
  const onDoneRef = useRef(onDone);
  onDoneRef.current = onDone;
  const scannedAt = useRef(new Date().toISOString());
  const locksRef = useRef<{ locationId: string; heldBy: string }[]>([]);
  useEffect(() => {
    const load = () => Api.countLocks().then((l) => { locksRef.current = l; }).catch(() => undefined);
    void load();
    const h = setInterval(() => void load(), 30_000);
    return () => clearInterval(h);
  }, []);

  const step = run.stepId ? FLOW.steps.find((s) => s.id === run.stepId) : null;
  const locationCode = (id: string | null) => (id ? registry.bundle.locations.find((l) => l.id === id)?.code ?? null : null);

  const complete = useCallback(
    async (state: RunState, destination: { id: string; code: string }, u: Unit) => {
      setBusy(true);
      // Bulk stock joins the row of the same product already at the place (owner 2026-11-03: one row per
      // product and location): the flow's Moved becomes Merged into that row.
      const target = await mergeTarget(u.container, destination.id);
      const merged = target ? { ...state, emitted: state.emitted.map((em) => (em.event === "moved" ? { event: "merged" as const, payload: { intoContainerId: target.container_id, fromContainerIds: (em.payload as { containerIds: string[] }).containerIds, fromLocationId: u.container.location_id, toLocationId: destination.id, quantity: { value: String(Number(target.quantity) + Number(partialRef.current?.qty ?? u.container.quantity)), uom: u.container.uom } } } : em)) } : state;
      const batch = buildEmittedBatch(merged, { settings, goodsOwnerId: registry.bundle.goodsOwner.id, readPoint: destination.id, flow: { id: FLOW.id, version: FLOW.version }, stepId: "move", captureDetail: target ? "merged" : null, occurredAt: scannedAt.current });
      const part = partialRef.current;
      if (part) {
        const c = part.parent;
        const split: EventEnvelope = {
          eventId: uuidv7(), tenantId: settings.tenantId, goodsOwnerId: registry.bundle.goodsOwner.id, type: "split", class: classOf("split"), schemaVersion: 1,
          occurredAt: scannedAt.current, actorId: settings.actorId, deviceId: settings.deviceId, capture: "manual", captureDetail: "loose-quantity", readPoint: c.location_id, bizLocation: null,
          flowId: FLOW.id, flowVersion: FLOW.version, batchId: (batch.events[0] as EventEnvelope).batchId,
          payload: {
            parentContainerId: c.container_id, childContainerId: part.childId,
            identity: { scheme: "gtin+lot", key: null, ai: {}, origin: "tenant" },
            productId: c.product_id, gtin: c.gtin,
            lot: c.lot_number ? { number: c.lot_number, bestBefore: null, expiry: null, productionDate: null, packagingDate: null } : null,
            serial: null,
            quantity: { value: part.qty, uom: c.uom, source: "entered" },
            remaining: { value: part.remaining, uom: c.uom, source: "inferred" },
            locationId: c.location_id, reason: "loose-move",
          },
        };
        batch.events.unshift(split);
      }
      let result: "stored" | "queued" = "queued";
      try {
        result = await submitStep(batch);
      } catch {
        result = "queued";
      }
      feedback("accepted");
      const from = locationCode(u.container.location_id) ?? "?";
      const detached = u.parent ? ` ${t("move.detached", { sscc: u.parent.sscc ? shortSscc(u.parent.sscc) : "", n: Math.max(0, kidsOf(u.parent) - 1) })}` : "";
      const joined = target ? ` ${t("move.merged", { q: fmtQty(target.quantity, target.uom), code: destination.code })}` : "";
      onDoneRef.current({ kind: result === "stored" ? "ok" : "warn", label: result === "stored" ? t("move.done") : t("move.doneQueued"), message: `${describeUnit(u)} · ${from} → ${destination.code}.${detached}${joined}` });
    },
    [settings, registry],
  );

  /** The kolli goes onto a pallet: one standing at the place, or a fresh one issued here. The
   *  events in order: the split when part of a loose quantity moves, the pallet's own birth when
   *  fresh (received here, then placed), off the old pallet, the move when the place changes,
   *  onto the pallet. Built by hand like the split: the flow definition knows only places. */
  const completeOnto = useCallback(
    async (u: Unit, pallet: { containerId: string; sscc: string | null; fresh: boolean }, location: { id: string; code: string } | null, after?: AutomationEventIn[]) => {
      setBusy(true);
      const batchId = uuidv7();
      const now = scannedAt.current;
      const mk = (type: EventType, payload: Record<string, unknown>, capture: "barcode" | "system" = "barcode"): EventEnvelope => ({
        eventId: uuidv7(), tenantId: settings.tenantId, goodsOwnerId: registry.bundle.goodsOwner.id, type, class: classOf(type), schemaVersion: 1,
        occurredAt: now, actorId: settings.actorId, deviceId: settings.deviceId, capture, captureDetail: capture === "system" ? "onto-pallet" : null, readPoint: location?.id ?? null, bizLocation: location?.id ?? null,
        flowId: FLOW.id, flowVersion: FLOW.version, batchId, payload,
      } as EventEnvelope);
      const events: EventEnvelope[] = [];
      const part = partialRef.current;
      if (part) {
        const c = part.parent;
        events.push(mk("split", {
          parentContainerId: c.container_id, childContainerId: part.childId,
          identity: { scheme: "gtin+lot", key: null, ai: {}, origin: "tenant" }, productId: c.product_id, gtin: c.gtin,
          lot: c.lot_number ? { number: c.lot_number, bestBefore: null, expiry: null, productionDate: null, packagingDate: null } : null, serial: null,
          quantity: { value: part.qty, uom: c.uom, source: "entered" }, remaining: { value: part.remaining, uom: c.uom, source: "inferred" },
          locationId: c.location_id, reason: "loose-move",
        }, "system"));
      }
      if (pallet.fresh && pallet.sscc) {
        events.push(mk("received", { containerId: pallet.containerId, kind: "logistic-unit", identity: { scheme: "sscc", key: `sscc:00=${pallet.sscc}`, ai: { "00": pallet.sscc }, origin: "tenant" }, productId: null, gtin: null, packagingLevel: "pallet", lot: null, serial: null, sscc: pallet.sscc, quantity: { value: "1", uom: "EA", source: "scanned" }, locationId: location?.id ?? null, parentContainerId: null, supplierPartyId: null, expectedReceiptId: null, deliveryId: null, stated: false }, "system"));
        if (location) events.push(mk("moved", { containerIds: [pallet.containerId], fromLocationId: null, toLocationId: location.id, proposedLocationId: null, overrideReason: null }, "system"));
      }
      if (u.parent && u.parent.container_id !== pallet.containerId) events.push(mk("disaggregated", { parentContainerId: u.parent.container_id, childContainerIds: [u.container.container_id] }));
      if (location && u.container.location_id !== location.id) events.push(mk("moved", { containerIds: [u.container.container_id], fromLocationId: u.container.location_id, toLocationId: location.id, proposedLocationId: null, overrideReason: null }));
      events.push(mk("aggregated", { parentContainerId: pallet.containerId, childContainerIds: [u.container.container_id], completeness: "verified" }));
      const batch: StepBatch = { flowId: FLOW.id, flowVersion: FLOW.version, stepId: "move", events, effects: [] };
      let result: "stored" | "queued" = "queued";
      try {
        result = await submitStep(batch, after);
      } catch {
        result = "queued";
      }
      setBusy(false);
      feedback("accepted");
      const from = locationCode(u.container.location_id) ?? "?";
      const sscc = pallet.sscc ? shortSscc(pallet.sscc) : pallet.containerId.slice(-6);
      const detached = u.parent && u.parent.container_id !== pallet.containerId ? ` ${t("move.detached", { sscc: u.parent.sscc ? shortSscc(u.parent.sscc) : "", n: Math.max(0, kidsOf(u.parent) - 1) })}` : "";
      onDoneRef.current({ kind: result === "stored" ? "ok" : "warn", label: result === "stored" ? t("move.done") : t("move.doneQueued"), message: `${describeUnit(u)} · ${from} → ${t(pallet.fresh ? "move.ontoNew" : "move.onto", { sscc, code: location?.code ?? "—" })}.${detached}` });
      return result;
    },
    [settings, registry],
  );

  /** A new pallet, issued here, at the given place: the kolli goes onto it. The label follows the
   *  automation for "pall påbörjas" in the move flow; with none set up the pallet label prints anyway,
   *  because a pallet without a label cannot be scanned again. */
  const newPallet = async (location: { id: string; code: string }) => {
    if (!unit) return;
    setOnto(null);
    setIssuing(true);
    try {
      // Room for one more pallet where it is built (the kolli's own pallet counts).
      const capacity = palletCapacityOf(registry, location.id);
      if (capacity > 0) {
        const there = (await Api.locationContents(location.id)).filter((c) => c.kind === "logistic-unit").length;
        if (there >= capacity) {
          feedback("rejected");
          setBanner({ kind: "bad", label: t("banner.rejected"), message: t("move.full", { code: location.code, n: there, cap: capacity }) });
          return;
        }
      }
      const { sscc } = await Api.issueSscc();
      const containerId = uuidv7();
      const started: AutomationEventIn = { type: "pallet.started", flow: "move", sscc };
      const result = await completeOnto(unit, { containerId, sscc, fresh: true }, location, [started]);
      if (result === "stored") {
        Api.automationEvent(started)
          .then((r) => (r.prints === 0 ? Api.printJob({ kind: "pallet-label", containerId }).then(() => undefined) : undefined))
          .catch(() => undefined);
      }
    } catch (e) {
      feedback("rejected");
      setBanner({ kind: "bad", label: t("banner.rejected"), message: t("move.issueFailed", { err: e instanceof Error ? e.message : String(e) }) });
    } finally {
      setIssuing(false);
    }
  };

  /** The flow asks where the unit stands and whether it sits on a pallet (inquiry step). */
  const answerWhere = useCallback((state: RunState, u: Unit): RunState => {
    const { state: next } = applyInput(FLOW, state, { kind: "answer", value: { parentContainerId: u.parent?.container_id ?? null, locationId: u.container.location_id } });
    runRef.current = next;
    setRun(next);
    return next;
  }, []);

  /** Start the run with a resolved container as the unit. */
  const takeUnit = useCallback(
    (c: ContainerSummary, elements: GS1Elements, parent: ContainerSummary | null) => {
      const u: Unit = { container: c, elements, parent };
      setUnit(u);
      const input: ScanInput = { kind: "scan", parsed: elements, resolved: { as: "container", containerId: c.container_id, containerKind: c.kind === "logistic-unit" ? "logistic-unit" : "trade-item", identityKey: c.identity_key } };
      const { state, effects } = applyInput(FLOW, startRun(FLOW).state, input);
      runRef.current = state;
      setRun(state);
      scannedAt.current = new Date().toISOString();
      if (effects.some((e): e is Extract<Effect, { kind: "inquiry" }> => e.kind === "inquiry")) answerWhere(state, u);
      if (parent) setBanner({ kind: "warn", label: t("move.onPallet", { sscc: parent.sscc ? shortSscc(parent.sscc) : "" }), message: t("move.onPallet.msg") });
      else setBanner(null);
    },
    [answerWhere],
  );

  const resolveUnit = useCallback(
    async (elements: GS1Elements): Promise<boolean> => {
      const key = elements.sscc ? `sscc:00=${elements.sscc}` : identityKey(SCHEME, elements);
      if (!key) {
        setBanner({ kind: "bad", label: t("banner.rejected"), message: t("putaway.notFound") });
        return false;
      }
      let rows: ContainerSummary[];
      try {
        rows = await Api.containersByIdentity(key);
      } catch (e) {
        setBanner({ kind: "bad", label: t("banner.rejected"), message: t("putaway.lookupFailed", { err: e instanceof Error ? e.message : String(e) }) });
        return false;
      }
      if (rows.length === 0) {
        setBanner({ kind: "bad", label: t("banner.rejected"), message: t("putaway.notFound") });
        return false;
      }
      if (rows.length > 1) {
        setBanner({ kind: "bad", label: t("banner.rejected"), message: t("putaway.several", { n: rows.length }) });
        return false;
      }
      const c = rows[0]!;
      if (c.kind === "logistic-unit" && (c.state === "emptied" || c.state === "shipped")) {
        setBanner({ kind: "bad", label: t("banner.rejected"), message: t("move.palletRetired", { sscc: c.sscc ? shortSscc(c.sscc) : "" }) });
        return false;
      }
      let parent: ContainerSummary | null = null;
      if (c.parent_id) {
        try {
          const p = await Api.container(c.parent_id);
          parent = { ...p, children: kidsOf(p) };
        } catch {
          parent = null;
        }
      }
      takeUnit(c, elements, parent);
      return true;
    },
    [takeUnit],
  );

  /** A carton or pallet tapped in the location list becomes the unit, as if scanned. */
  const takeFromList = (c: ContainerSummary) => {
    takeUnit(c, plainElements(c.identity_key ?? c.container_id, null), null);
    setHere(null);
    setLoose(null);
  };
  /** Loose stock with the chosen quantity: all of it moves as the unit; part of it is cut off first. */
  const startLoose = () => {
    if (!loose) return;
    const have = Number(loose.c.quantity);
    const qty = Number(loose.qty.replace(",", "."));
    if (!Number.isFinite(qty) || qty <= 0 || qty > have) {
      setBanner({ kind: "bad", label: t("banner.rejected"), message: t("move.qtyBad", { have: fmtQty(loose.c.quantity, loose.c.uom) }) });
      return;
    }
    if (qty >= have) {
      partialRef.current = null;
      takeFromList(loose.c);
      return;
    }
    const childId = uuidv7();
    const str = (n: number) => String(Math.round(n * 1000) / 1000);
    partialRef.current = { parent: loose.c, childId, qty: str(qty), remaining: str(have - qty) };
    takeUnit({ ...loose.c, container_id: childId, quantity: str(qty) }, plainElements(childId, null), null);
    setHere(null);
    setLoose(null);
  };

  /** An empty pallet scanned as the unit can be retired by hand (owner 2026-11-02: for sites that keep empty pallets, or a pallet emptied before the rule). */
  const retireEmpty = async () => {
    if (!unit || unit.container.kind !== "logistic-unit" || kidsOf(unit.container) > 0) return;
    setBusy(true);
    const ev: EventEnvelope = { eventId: uuidv7(), tenantId: settings.tenantId, goodsOwnerId: registry.bundle.goodsOwner.id, type: "emptied", class: classOf("emptied"), schemaVersion: 1, occurredAt: new Date().toISOString(), actorId: settings.actorId, deviceId: settings.deviceId, capture: "barcode", captureDetail: null, readPoint: unit.container.location_id, bizLocation: null, flowId: FLOW.id, flowVersion: FLOW.version, batchId: uuidv7(), payload: { containerIds: [unit.container.container_id], source: "manual", reason: null } } as EventEnvelope;
    let result: "stored" | "queued" = "queued";
    try { result = await submitStep({ flowId: FLOW.id, flowVersion: FLOW.version, stepId: "unit", events: [ev], effects: [] }); } catch { result = "queued"; }
    setBusy(false);
    feedback("accepted");
    onDoneRef.current({ kind: result === "stored" ? "ok" : "warn", label: t("move.retired"), message: describeUnit(unit) });
  };

  /** "Hela pallen": the pallet the kolli sits on becomes the unit, no rescan. */
  const wholePallet = () => {
    if (!unit?.parent) return;
    const p = unit.parent;
    takeUnit({ ...p, children: kidsOf(p) }, plainElements(p.sscc ?? p.container_id, null), null);
  };

  /** The destination is the place itself: the flow takes the location scan and completes (detach + move). */
  const finishToLocation = useCallback(
    (before: RunState, code: BundleLocation, elements: GS1Elements): CodeState => {
      if (!unit) return "rejected";
      if (code.id === unit.container.location_id && !unit.parent) {
        feedback("rejected");
        setBanner({ kind: "bad", label: t("banner.rejected"), message: t("move.samePlace", { code: code.code }) });
        return "rejected";
      }
      const input: ScanInput = { kind: "scan", parsed: elements, resolved: { as: "location", locationId: code.id, roles: code.roles } };
      const { state, effects } = applyInput(FLOW, before, input);
      const fb = effects.find((e): e is Extract<Effect, { kind: "feedback" }> => e.kind === "feedback");
      if (fb?.result !== "accepted") {
        feedback("rejected");
        setBanner({ kind: "bad", label: t("banner.rejected"), message: t("move.notAllowed", { code: code.code }) });
        return "rejected";
      }
      runRef.current = state;
      setRun(state);
      if (effects.some((e) => e.kind === "completed")) void complete(state, { id: code.id, code: code.code }, unit);
      return "accepted";
    },
    [unit, complete],
  );

  /** A pallet in the choice sheet: the kolli goes onto it (its own pallet means nothing to do). */
  const choosePallet = (p: ContainerSummary) => {
    if (!unit || !onto) return;
    const loc = onto.location;
    setOnto(null);
    if (unit.parent?.container_id === p.container_id) {
      feedback("rejected");
      setBanner({ kind: "bad", label: t("banner.rejected"), message: t("move.onto.samePallet", { sscc: p.sscc ? shortSscc(p.sscc) : "" }) });
      return;
    }
    void completeOnto(unit, { containerId: p.container_id, sscc: p.sscc, fresh: false }, { id: loc.id, code: loc.code });
  };

  const handleRaw = useCallback(
    async (data: string): Promise<CodeState> => {
      const before = runRef.current;
      const aim = /^\]\w\w/.exec(data)?.[0] ?? null;
      const text = aim ? data.slice(3) : data;
      let elements: GS1Elements | null = null;
      if (!(aim && !isGs1Symbology(data))) {
        try {
          elements = await parseScan(data);
        } catch {
          elements = null;
        }
      }
      if (before.stepId === "unit") {
        if (!elements || (!elements.sscc && !elements.gtin)) {
          // A location: list what stands there, cartons to scan and loose stock to pick.
          const loc = (elements?.ai["254"] ? registry.locationByGln.get(elements.ai["254"]) : findLocation(registry, text)) ?? null;
          if (loc) {
            setBusy(true);
            try {
              const contents = await Api.locationContents(loc.id);
              setHere({ location: loc, contents });
              setLoose(null);
              setBanner(null);
              feedback("accepted");
              return "accepted";
            } catch (e) {
              setBanner({ kind: "bad", label: t("banner.rejected"), message: t("putaway.lookupFailed", { err: e instanceof Error ? e.message : String(e) }) });
              feedback("rejected");
              return "rejected";
            } finally {
              setBusy(false);
            }
          }
          feedback("rejected");
          setBanner({ kind: "bad", label: t("banner.rejected"), message: t("putaway.notFound") });
          return "rejected";
        }
        setBusy(true);
        const ok = await resolveUnit(elements);
        setBusy(false);
        feedback(ok ? "accepted" : "rejected");
        return ok ? "accepted" : "rejected";
      }
      if (before.stepId === "destination" && unit) {
        // A pallet scanned as the destination (owner 2026-11-02): the kolli goes onto it, where it stands.
        if (elements?.sscc) {
          if (unit.container.kind === "logistic-unit") {
            feedback("rejected");
            setBanner({ kind: "bad", label: t("banner.rejected"), message: t("move.palletOnPallet") });
            return "rejected";
          }
          setBusy(true);
          let rows: ContainerSummary[] = [];
          try {
            rows = await Api.containersByIdentity(`sscc:00=${elements.sscc}`);
          } catch (e) {
            setBusy(false);
            feedback("rejected");
            setBanner({ kind: "bad", label: t("banner.rejected"), message: t("putaway.lookupFailed", { err: e instanceof Error ? e.message : String(e) }) });
            return "rejected";
          }
          setBusy(false);
          const p = rows.find((r) => r.kind === "logistic-unit") ?? null;
          if (!p) {
            feedback("rejected");
            setBanner({ kind: "bad", label: t("banner.rejected"), message: t("move.palletUnknown", { sscc: shortSscc(elements.sscc) }) });
            return "rejected";
          }
          if (p.state === "emptied" || p.state === "shipped") {
            feedback("rejected");
            setBanner({ kind: "bad", label: t("banner.rejected"), message: t("move.palletRetired", { sscc: shortSscc(elements.sscc) }) });
            return "rejected";
          }
          if (unit.parent?.container_id === p.container_id) {
            feedback("rejected");
            setBanner({ kind: "bad", label: t("banner.rejected"), message: t("move.onto.samePallet", { sscc: shortSscc(elements.sscc) }) });
            return "rejected";
          }
          if (p.location_id) {
            const lock = locksRef.current.find((k) => k.locationId === p.location_id && k.heldBy !== settings.actorId);
            if (lock) {
              feedback("rejected");
              setBanner({ kind: "bad", label: t("count.locked"), message: t("count.destinationLocked", { code: locationCode(p.location_id) ?? "?", who: lock.heldBy }) });
              return "rejected";
            }
          }
          void completeOnto(unit, { containerId: p.container_id, sscc: p.sscc, fresh: false }, p.location_id ? { id: p.location_id, code: locationCode(p.location_id) ?? "?" } : null);
          return "accepted";
        }
        const code = (elements?.ai["254"] ? registry.locationByGln.get(elements.ai["254"]) : findLocation(registry, text)) ?? null;
        if (!code) {
          feedback("rejected");
          setBanner({ kind: "bad", label: t("banner.rejected"), message: missingLocationMessage(registry, text) ?? t("move.destUnknown", { raw: text.slice(0, 40) }) });
          return "rejected";
        }
        // A location under count takes nothing until its counter is done (owner 2026-10-28); the server refuses too.
        const lock = locksRef.current.find((k) => k.locationId === code.id && k.heldBy !== settings.actorId);
        if (lock) {
          feedback("rejected");
          setBanner({ kind: "bad", label: t("count.locked"), message: t("count.destinationLocked", { code: code.code, who: lock.heldBy }) });
          return "rejected";
        }
        // Temperature (owner 2026-10-31): a product needing another storage class than the place has.
        // A pallet is checked by its contents too (owner 2026-10-31): its cartons' products are looked up once.
        let kidProducts: (string | null)[] = [];
        if (unit?.container.kind === "logistic-unit") { try { kidProducts = (((await Api.container(unit.container.container_id)).children ?? []) as { product_id: string | null }[]).map((k) => k.product_id); } catch { kidProducts = []; } }
        const mm = unit ? storageMismatchAny(registry, [unit.container.product_id, ...kidProducts], code.id) : null;
        if (mm?.action === "refuse") {
          feedback("rejected");
          setBanner({ kind: "bad", label: t("storage.wrong"), message: t("storage.wrongMsg", { need: mm.need, has: mm.has, code: code.code }) });
          return "rejected";
        }
        if (mm) setBanner({ kind: "warn", label: t("storage.wrong"), message: t("storage.wrongMsg", { need: mm.need, has: mm.has, code: code.code }) });
        // What the place takes (owner 2026-11-02): a pallet cannot go where only loose goods stand; a
        // kolli going where only pallets stand, or where pallets already stand, is asked which pallet.
        const holds = holdsOf(registry, code.id);
        const capacity = palletCapacityOf(registry, code.id);
        let pallets: ContainerSummary[] = [];
        setBusy(true);
        try {
          pallets = (await Api.locationContents(code.id)).filter((c) => c.kind === "logistic-unit");
        } catch {
          pallets = [];
        } finally {
          setBusy(false);
        }
        if (unit.container.kind === "logistic-unit") {
          if (holds === "loose") {
            feedback("rejected");
            setBanner({ kind: "bad", label: t("banner.rejected"), message: t("move.noPalletsHere", { code: code.code }) });
            return "rejected";
          }
          // Room for the pallet (owner 2026-11-02: two pallets cannot share a compartment).
          const others = pallets.filter((p) => p.container_id !== unit.container.container_id).length;
          if (capacity > 0 && others >= capacity) {
            feedback("rejected");
            setBanner({ kind: "bad", label: t("banner.rejected"), message: t("move.full", { code: code.code, n: others, cap: capacity }) });
            return "rejected";
          }
        } else if (holds === "pallets" || pallets.length > 0) {
          setOnto({ location: code, pallets, holds, capacity });
          feedback("accepted");
          return "accepted";
        }
        return finishToLocation(before, code, elements ?? plainElements(text, aim));
      }
      feedback("rejected");
      return "rejected";
    },
    [registry, resolveUnit, finishToLocation, completeOnto, settings.actorId, unit],
  );

  const onCameraCode = useCallback(async (data: string) => {
    const st = await handleRaw(data);
    if (st === "accepted") setCamera(false);
    return st;
  }, [handleRaw]);

  useEffect(() => {
    const el = inputRef.current;
    if (!el || camera) return;
    focusScan(el);
    return attachWedge(el, { onScan: (d) => void handleRaw(d) });
  }, [handleRaw, camera]);
  useEffect(() => {
    if (!camera) focusScan(inputRef.current);
  }, [step?.id, camera]);

  if (camera) return <CameraView onCode={onCameraCode} onClose={() => setCamera(false)} />;

  const isUnitStep = step?.id === "unit";
  const cells: LabelCell[] = unit ? unitCells(unit) : [];
  const unitName = unit ? (unit.container.product_id ? registry.bundle.products.find((p) => p.id === unit.container.product_id)?.name ?? t("label.unknownProduct") : unit.container.kind === "logistic-unit" ? t("unit.pallet") : t("label.unknownProduct")) : "";
  const from = unit ? locationCode(unit.container.location_id) ?? "—" : "";

  return (
    <div className="screen" onClick={() => { unlockAudio(); focusScan(inputRef.current); }}>
      <Strip left={<b>{t("menu.move")}</b>} middle={unit ? (unit.container.sscc ? `${t("unit.pallet")} ${shortSscc(unit.container.sscc)}` : `${unit.container.kind === "loose" ? t("unit.loose") : t("unit.kolli")} ${unit.container.lot_number ?? ""}`) : here ? here.location.code : undefined} {...net} />
      <div className="task">
        <div className="task-main">
          {!isUnitStep && banner && banner.kind === "warn" && <Banner {...banner} />}
          {isUnitStep && here ? (
            <Prompt step={t("putaway.step1")} title={t("move.whatMoves")} sub={here.contents.length ? t("move.hereSub", { code: here.location.code, cartons: here.contents.filter((c) => c.kind !== "loose").length, loose: here.contents.filter((c) => c.kind === "loose").length }) : t("move.hereEmpty", { code: here.location.code })} />
          ) : isUnitStep ? <Prompt step={t("putaway.step1")} title={t("putaway.title1")} /> : <Prompt step={t("putaway.step2")} title={t("move.title2")} />}
          {!isUnitStep && (
            <div className="from-to"><span>{from}</span><span className="arrow">→</span><span className="pending">{t("move.newPlace")}</span></div>
          )}
          <ScanBar inputRef={inputRef} placeholder={t("scan.ready")} onCamera={() => { unlockAudio(); setCamera(true); }} />
          {banner && (isUnitStep || banner.kind !== "warn") && <Banner {...banner} />}
          {isUnitStep && here && here.contents.length > 0 && (
            <Rows compact rows={here.contents.map((c): RowData => {
              const name = c.product_id ? registry.bundle.products.find((p) => p.id === c.product_id)?.name ?? t("label.unknownProduct") : c.kind === "logistic-unit" ? t("unit.pallet") : t("label.unknownProduct");
              const isLoose = c.kind === "loose";
              return {
                k: name,
                small: `${isLoose ? t("unit.loose") : c.kind === "logistic-unit" ? t("unit.pallet") : t("unit.kolli")}${c.lot_number ? ` · ${c.lot_number}` : ""}${c.sscc ? ` · ${shortSscc(c.sscc)}` : ""}`,
                q: c.kind === "logistic-unit" ? t("rows.kolli", { n: c.children }) : fmtQty(c.quantity, c.uom),
                selected: loose?.c.container_id === c.container_id,
                onClick: () => (isLoose ? setLoose({ c, qty: c.quantity }) : takeFromList(c)),
              };
            })} />
          )}
          {isUnitStep && loose && (
            <label className="field"><span>{t("move.qty")} <small>({loose.c.uom})</small></span>
              <input className="input" inputMode="decimal" value={loose.qty} onChange={(e) => setLoose({ ...loose, qty: e.target.value })} onKeyDown={(e) => { if (e.key === "Enter") startLoose(); }} />
              <span className="hint">{t("move.qtyHint")}</span>
            </label>
          )}
          {unit ? (
            <LabelStrip title={unit.container.kind === "logistic-unit" && !unit.container.product_id ? t("unit.pallet") : `${unit.container.kind === "logistic-unit" ? t("unit.pallet") : unit.container.kind === "loose" ? t("unit.loose") : t("unit.kolli")} · ${unitName}`} sub={unit.container.kind === "logistic-unit" ? t("rows.kolli", { n: unit.container.children }) : unit.container.kind === "loose" ? (partialRef.current ? t("move.looseOf", { qty: partialRef.current.qty, have: partialRef.current.parent.quantity, uom: unit.container.uom }) : t("move.looseAll")) : unit.parent ? t("move.oneOf", { n: kidsOf(unit.parent) }) : t("label.last")} cells={cells} />
          ) : !here ? (
            <LabelStrip empty={t("move.hint")} />
          ) : null}
          {busy && <p className="hint">{t("common.loading")}</p>}
        </div>
      </div>
      <Actions>
        <Btn kind="quiet" icon="back" onClick={onClose}>{t("common.menu")}</Btn>
        {/* Print on demand from the phone (owner 2026-10-30; templates 2026-11-01): the scanned pallet's label, or the scanned location's. */}
        {isUnitStep && here && <Btn kind="secondary" onClick={() => void printLabel({ kind: "location-label", locationId: here.location.id })}>{t("move.printLabel")}</Btn>}
        {isUnitStep && !here && unit?.container.kind === "logistic-unit" && <Btn kind="secondary" onClick={() => void printLabel({ kind: "pallet-label", containerId: unit.container.container_id })}>{t("move.printLabel")}</Btn>}
        {isUnitStep && !here && unit?.container.kind === "trade-item" && <Btn kind="secondary" onClick={() => void printLabel({ kind: "customer-label", containerId: unit.container.container_id })}>{t("move.printCustomerLabel")}</Btn>}
        {!isUnitStep && unit?.container.kind === "logistic-unit" && kidsOf(unit.container) === 0 && <Btn kind="danger" disabled={busy} onClick={() => void retireEmpty()}>{t("move.retireEmpty")}</Btn>}
        {!isUnitStep && unit?.parent && <Btn kind="secondary" onClick={wholePallet}>{t("move.wholePallet")}</Btn>}
        {/* A new pallet where the kolli stands (owner 2026-11-02): the third way besides a place and the whole pallet. */}
        {!isUnitStep && unit && unit.container.kind !== "logistic-unit" && unit.container.location_id && <Btn kind="secondary" disabled={issuing || busy} onClick={() => { const id = unit.container.location_id!; void newPallet({ id, code: locationCode(id) ?? "?" }); }}>{issuing ? t("pallet.issuing") : t("move.newPalletHere")}</Btn>}
        {isUnitStep && loose && <Btn kind="primary" onClick={startLoose}>{t("move.toPlace")}</Btn>}
      </Actions>
      {onto && unit && (
        <Sheet title={t("move.onto.title", { code: onto.location.code })} onClose={() => setOnto(null)}>
          <p className="hint">{onto.holds === "pallets" ? t("move.onto.palletsOnly", { code: onto.location.code }) : t("move.onto.hasPallets", { n: onto.pallets.length })}{onto.capacity > 0 ? ` ${t("move.onto.room", { n: onto.pallets.length, cap: onto.capacity })}` : ""}</p>
          {onto.pallets.length > 0 && (
            <Rows compact rows={onto.pallets.map((p): RowData => ({
              k: `${t("unit.pallet")} ${p.sscc ? shortSscc(p.sscc) : p.container_id.slice(-6)}`,
              small: unit.parent?.container_id === p.container_id ? t("move.onto.current") : (p.product_id ? registry.bundle.products.find((x) => x.id === p.product_id)?.name : undefined),
              q: t("rows.kolli", { n: kidsOf(p) }),
              onClick: () => choosePallet(p),
            }))} />
          )}
          <Actions>
            {(onto.capacity === 0 || onto.pallets.length < onto.capacity) && <Btn kind="primary" disabled={issuing} onClick={() => void newPallet({ id: onto.location.id, code: onto.location.code })}>{issuing ? t("pallet.issuing") : t("move.newPallet")}</Btn>}
            {onto.holds !== "pallets" && <Btn kind="secondary" onClick={() => { const l = onto.location; setOnto(null); finishToLocation(runRef.current, l, plainElements(l.code, null)); }}>{t("move.onto.loose")}</Btn>}
          </Actions>
        </Sheet>
      )}
    </div>
  );
}

function unitCells(u: Unit): LabelCell[] {
  const c = u.container;
  const cells: LabelCell[] = [];
  if (c.sscc) cells.push({ title: "SSCC", value: c.sscc, wide: true });
  else if (c.gtin) cells.push({ title: "GTIN", value: c.gtin, wide: true });
  if (c.kind === "logistic-unit") cells.push({ title: "Count", value: <>{c.children} <small>{t("unit.kolli").toLowerCase()}</small></> });
  else {
    cells.push({ title: "Batch/Lot", value: c.lot_number ?? <small>{t("label.missing")}</small> });
    cells.push({ title: "Content", value: fmtQty(c.quantity, c.uom) });
  }
  return cells;
}

/** The loose row of the same product standing at the destination, when the moving unit is bulk stock (owner 2026-11-03). */
export async function mergeTarget(c: ContainerSummary, destinationId: string): Promise<ContainerSummary | null> {
  if (c.kind !== "loose" || !c.product_id) return null;
  try {
    const there = await Api.locationContents(destinationId);
    // Only rows of the same ERP row (the technical lot) join: the ERP's balance per row must stay right (owner 2026-11-03).
    return there.find((x) => x.kind === "loose" && x.product_id === c.product_id && x.uom === c.uom && (x.lot_number ?? null) === (c.lot_number ?? null) && x.container_id !== c.container_id && !x.parent_id && (x.state === undefined || x.state === "received" || x.state === "stored")) ?? null;
  } catch {
    return null;
  }
}

function describeUnit(u: Unit): string {
  const c = u.container;
  return c.sscc ? `${t("unit.pallet")} ${shortSscc(c.sscc)}` : `${c.kind === "loose" ? t("unit.loose") : t("unit.kolli")} ${c.kind === "loose" ? "" : c.lot_number ?? c.gtin ?? ""}${c.kind === "loose" ? ` · ${fmtQty(c.quantity, c.uom)}` : ""}`;
}
